import { useCallback, useEffect, useRef } from 'react';
import { DWELL_MS, FLUSH_DEBOUNCE_MS, VISIBILITY_RATIO } from '../lib/unreadConstants';

/**
 * Mark comments read once they have genuinely been looked at.
 *
 * A comment counts as read after DWELL_MS continuous milliseconds at
 * VISIBILITY_RATIO or more of its box inside the viewport, with the tab
 * active. Per-comment rather than per-task: opening a thread of forty
 * comments must not clear the one you never scrolled to.
 *
 * Usage:
 *   const registerComment = useCommentReadObserver({ unreadCommentIds, onRead });
 *   <div ref={registerComment(comment.id)}>...</div>
 */
export const useCommentReadObserver = ({ unreadCommentIds, onRead }) => {
  const observerRef = useRef(null);
  const elementToId = useRef(new Map());
  const timers = useRef(new Map());
  const pending = useRef(new Set());
  const flushTimer = useRef(null);

  // Kept in refs so the observer callback never closes over a stale render.
  const unreadRef = useRef(new Set(unreadCommentIds));
  const onReadRef = useRef(onRead);
  useEffect(() => { unreadRef.current = new Set(unreadCommentIds); }, [unreadCommentIds]);
  useEffect(() => { onReadRef.current = onRead; }, [onRead]);

  const flush = useCallback(() => {
    if (flushTimer.current) {
      clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    if (pending.current.size === 0) return;
    const batch = Array.from(pending.current);
    pending.current.clear();
    onReadRef.current?.(batch);
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  }, [flush]);

  const clearTimer = useCallback((id) => {
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const startTimer = useCallback((id) => {
    if (timers.current.has(id)) return;      // already counting
    if (!unreadRef.current.has(id)) return;  // nothing to clear
    const handle = setTimeout(() => {
      timers.current.delete(id);
      unreadRef.current.delete(id);
      pending.current.add(id);
      scheduleFlush();
    }, DWELL_MS);
    timers.current.set(id, handle);
  }, [scheduleFlush]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (document.visibilityState !== 'visible') return;
      entries.forEach((entry) => {
        const id = elementToId.current.get(entry.target);
        if (!id) return;
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_RATIO) {
          startTimer(id);
        } else {
          clearTimer(id);
        }
      });
    }, { threshold: [VISIBILITY_RATIO] });

    observerRef.current = observer;
    // Refs attach during commit, BEFORE this effect runs, so on first mount
    // every comment registers while observerRef is still null. Pick them up.
    elementToId.current.forEach((_id, element) => observer.observe(element));

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [startTimer, clearTimer]);

  // A backgrounded tab must not accumulate dwell time. Timers restart from
  // zero when the user comes back, deliberately: a task left open overnight
  // should not clear anything.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        timers.current.forEach((handle) => clearTimeout(handle));
        timers.current.clear();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Closing the modal right after a dwell fired must still persist the read.
  useEffect(() => () => {
    timers.current.forEach((handle) => clearTimeout(handle));
    timers.current.clear();
    flush();
  }, [flush]);

  return useCallback((commentId) => (element) => {
    if (!element) return;
    elementToId.current.set(element, commentId);
    observerRef.current?.observe(element);
  }, []);
};

export default useCommentReadObserver;
